import { Button, Col, Modal, Row, Form } from "react-bootstrap"
import { useContext, useEffect, useState } from "react"
import { AssignOption, ClassResponse, ReviewGroup, StudentResponse } from "shared/types/teacherTypes"
import * as teacher from "shared/routes/teacher"
import SelectStudents from "./SelectStudents"
import SelectNoneAll from "./BulkComponents/SelectNoneAll"
import AssignOptionToggle from "./BulkComponents/AssignOptionToggle"
import { getReviewGroups } from "shared/routes/reviewRoutes"
import { useModel } from "@stem-sims/nexus"
import { AssessmentViewOptions, AssessmentViewOptionsType } from "shared/types/assessmentTypes"
import { BeatLoader } from "react-spinners"
import moment from "moment"
import { bulkCreateReviewAssignments } from "shared/routes/science35"
import { toast } from "react-toastify"
import SelectGradeLevelDropdown from "components/Dashboards/General/Science35/SelectGradeLevelDropdown"
import { BlockType, ReviewGradeType, FilterGradeLevel, EnableReadingLevelBlocks } from "shared/types/science35Types"
import ToolTip from "components/General/ToolTip"
import styles from "./assignReviewForm.module.scss"
import { AuthContext } from "AuthContext"

export interface Props {
    show: boolean
    onHide: () => void
    activeClass: ClassResponse
    selectedExistingGrades: ReviewGradeType[]
    blockType: BlockType
    filterGrades: Readonly<FilterGradeLevel[]>
}

const AssignReviewModal = ({ filterGrades, show, onHide, activeClass, selectedExistingGrades, blockType } : Props) => {
    const [assignOption, setAssignOption] = useState<AssignOption>("class")
    const [classStudents, setClassStudents] = useState<StudentResponse[]>([])
    const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([])
    const [selectedAssessmentView, setSelectedAssessmentView] = useState<AssessmentViewOptionsType>("Answers")
    const [oneQuestionMode, setOneQuestionMode] = useState(true)
    const [randomizable, setRandomizable] = useState(false)
    const [useGrade3, setUseGrade3] = useState(false)
    const [dueDate, setDueDate] = useState<string | null>(null)

    const [selectedGrades, setSelectedGrades] = useState<ReviewGradeType[]>([])
    const [selectedReviewIds, setSelectedReviewIds] = useState<string[]>([])
    const [selectedGroups, setSelectedGroups] = useState<string[]>([])

    const { scienceBlocks } = useContext(AuthContext)

    useEffect(() => {
        setSelectedGrades(selectedExistingGrades)
    }, [selectedExistingGrades])

    const { response: groups, loading: groupsLoading } = useModel({
        model: getReviewGroups,
        props: { applyFilter: true, gradeLevels: selectedGrades, classGradeLevel: activeClass?.gradeLevel, classId: activeClass?.id, targetBlock: blockType },
        deps: [show]
    })

    useEffect(() => {
        if (activeClass?.id) {
            teacher.getStudents({ classID: activeClass.id }).then(setClassStudents)
        } else {
            setClassStudents(null)
        }
    }, [activeClass?.id])

    const onAssign = () => {
        bulkCreateReviewAssignments({ 
            classId: activeClass.id, 
            reviewIds: selectedReviewIds,
            blockType: blockType,
            dueDate: dueDate ? moment(dueDate).utc().toDate() : undefined,
            assessmentViewOption: selectedAssessmentView,
            oneQuestionMode: oneQuestionMode,
            randomizable: randomizable,
            useGrade3: useGrade3,
            studentIds: assignOption === "students" ? selectedStudentIds : undefined,
        })
        .then(res => {
            if (res.skipped.length > 0) {
                toast.warn("Successfully created review assignments, but some reviews were skipped because they could not be found.")
            } else {
                toast.success("Successfully created review assignments.")
            }
        })
        .catch(() => {
            toast.error("Something went wrong creating review assignments. Please try again.")
        })
        .finally(() => {
            setSelectedStudentIds([])
            onHide()
            setDueDate(null)
            setSelectedReviewIds([])
        })
    }

    const hideAssignModal = () => {
        setSelectedStudentIds([])
        setSelectedReviewIds([])
        setSelectedGroups([])
        setDueDate(null)
        onHide()
    }

    function toggleGroup(group: ReviewGroup) {
        if (selectedGroups.includes(group.groupId)) {
            setSelectedGroups(prev => prev.filter(id => id !== group.groupId))
            setSelectedReviewIds(prev => prev.filter(id => !group.reviews.some(review => review.id === id)))
        } else {
            setSelectedGroups(prev => [...prev, group.groupId])
            setSelectedReviewIds(prev => [...prev, ...group.reviews.map(review => review.id)])
        }
    }

    function toggleReview(reviewId: string) {
        setSelectedReviewIds(prevReviewIds => {
            const nextReviewIds = prevReviewIds.includes(reviewId) ? prevReviewIds.filter(id => id !== reviewId) : [...prevReviewIds, reviewId]

            const reviewIdSet = new Set(nextReviewIds)

            const nextSelectedGroups = groups
            .filter(group =>
                group.reviews.every(review => reviewIdSet.has(review.id))
            )
            .map(group => group.groupId)

            setSelectedGroups(nextSelectedGroups)

            return nextReviewIds
        })
    }

    if (groupsLoading || !groups) return <BeatLoader />

    const submitEnabled = selectedReviewIds.length > 0 && (selectedStudentIds.length > 0 || assignOption === "class")
    const validFilterGrades = filterGrades.filter(grade => grade.isValid)

    const readingLevelEnabled = scienceBlocks.some(a => EnableReadingLevelBlocks.includes(a))

    return( 
        <Modal show={show} onHide={hideAssignModal} size="xl">
            <Modal.Header className="border-0" closeButton>
                <h3 className="w-100 text-center">Select Reviews to Assign</h3>
            </Modal.Header>
            <Modal.Body className="mx-5 text-center">
                {validFilterGrades.length > 0 && 
                    <Row className="my-2">
                        <SelectGradeLevelDropdown grades={filterGrades} selectedGrades={selectedGrades} onSelect={setSelectedGrades} />
                    </Row>}
                <Row>
                    <Col className="overflow-auto" style={{ height: "75vh" }}>
                        {groups.length === 0 && <div className="text-muted fw-bold">No review groups found. Try selecting a different grade level.</div>}
                        {groups.map(group => (
                            <div key={group.name} className="mb-3 text-start">
                                {group.name && 
                                <h4 
                                    role="button"
                                    style={{userSelect: "none"}}
                                    className={`${styles.groupName} border-bottom border-dark p-1 ${selectedGroups.includes(group.groupId) ? styles.selectedGroup : "text-muted"}`}
                                    onClick={() => toggleGroup(group)}>
                                        {group.name}
                                </h4>}
                                {group.reviews.map(review => (
                                    <div 
                                        role="button"
                                        style={{userSelect: "none"}} //prevents user from selecting text
                                        key={review.id} 
                                        className={`mt-1 ms-3 border p-1 rounded ${selectedReviewIds.includes(review.id) ? "bg-primary text-white" : "bg-light"}`}
                                        onClick={() => toggleReview(review.id)}>
                                            <ToolTip title={review.description ?? review.title}><span>{review.title}: <span dangerouslySetInnerHTML={{ __html: review.label}}></span></span></ToolTip>
                                    </div>))}
                            </div>
                        ))}
                    </Col>
                    <Col>
                        <h5 className="text-start">Student Results View</h5>
                        <Form.Select
                            value={selectedAssessmentView}
                            className="mb-3"
                            defaultValue={selectedAssessmentView}
                            onChange={e => setSelectedAssessmentView(e.target.value as AssessmentViewOptionsType)}
                        >
                            {AssessmentViewOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </Form.Select>
                        <h5 className="text-start">One Question Mode</h5>
                        <Form.Select
                            value={oneQuestionMode ? "Yes" : "No"}
                            className="mb-3"
                            onChange={e => setOneQuestionMode(e.target.value === "Yes")}>
                            {["Yes", "No"].map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </Form.Select>
                        <h5 className="text-start">Randomize Assessment Answers</h5>
                        <Form.Select
                            className="mb-3"
                            value={randomizable ? "true" : "false"}
                            onChange={(e) => setRandomizable(e.currentTarget.value === "true")}
                        >
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </Form.Select>
                        {readingLevelEnabled && <>
                            <h5 className="text-start">Reading Level</h5>
                            <Form.Select
                                className="mb-1"
                                value={useGrade3 ? "true" : "false"}
                                onChange={(e) => setUseGrade3(e.currentTarget.value === "true")}
                            >
                                <option value="true">Grade 3</option>
                                <option value="false">Grade 5</option>
                            </Form.Select>
                            <p className="text-start fw-bold text-muted">Adjusts text complexity for selected assignments</p>
                        </>}
                        <h5 className="text-start">Assign to</h5>
                        <AssignOptionToggle
                            onClassChosen={() => {
                                setAssignOption("class")
                                setSelectedStudentIds([])
                            }}
                            onStudentsChosen={() => {
                                setAssignOption("students")
                            }}
                            assignOption={assignOption}
                        />
                        
                        {assignOption === "class" &&
                            <div className="mt-2">
                                <p className="fw-bold text-muted text-start">Selected assignments will be assigned to all students in {activeClass.className}.</p>
                            </div>}

                        {assignOption === "students" &&
                            <div>
                                <p className="fw-bold text-muted mt-2 text-start">
                                    {classStudents.length === 0 ? `${activeClass.className} has no students.` : `Selected assignments will be assigned to all the students you select below.`}
                                </p>
                                {classStudents.length !== 0 &&
                                    <SelectNoneAll
                                        selectNone={() => {
                                            setSelectedStudentIds([])
                                        }} 
                                        selectAll={() => {
                                            const classStudentIds = classStudents.map(cs => cs.uuid)
                                            setSelectedStudentIds(classStudentIds)
                                        }}/>}
                                <div className="mt-3">
                                    <SelectStudents
                                            classStudents={classStudents}
                                            selectedStudentIds={selectedStudentIds}
                                            onSelectStudent={newStudents => setSelectedStudentIds(newStudents)}
                                            onUnselectStudent={newStudents => setSelectedStudentIds(newStudents)}
                                        />
                                </div>
                            </div>}

                            <h5 className="text-start">Due Date</h5>
                            <Form.Control
                                type="date"
                                onChange={(e) => setDueDate(e.currentTarget.value)}
                                max="9999-12-31"
                            />
                            <Button variant="primary" className="my-3 w-50" disabled={!submitEnabled} onClick={onAssign}>Assign</Button>
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    )
}

export default AssignReviewModal
